import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Input, Space, Tag, Modal, Form, message,
  Drawer, Descriptions, Switch, Typography, Row, Col, Statistic, Popconfirm, Select
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined,
  BankOutlined, TeamOutlined, ReloadOutlined, DeleteOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import { MobileDataList } from '../../../../components/mobile';
import superAdminApi, { AdminCompany, AdminPlan, PaginationParams } from '../../api/superAdminApi';

const { Title, Text } = Typography;

const CompanyManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<AdminCompany | null>(null);
  const [companyStats, setCompanyStats] = useState<any>(null);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [form] = Form.useForm();

  const fetchCompanies = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getCompanies({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        search: searchText || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setCompanies(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, [searchText, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSearch = () => {
    fetchCompanies({ page: 1 });
  };

  const handleTableChange = (paginationConfig: any) => {
    fetchCompanies({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  useEffect(() => {
    // Plans populate the Create Company plan picker.
    superAdminApi
      .getPlans()
      .then((res) => {
        if (res.success) setPlans(res.data || []);
      })
      .catch(() => {
        // Non-fatal: the admin can still create the company and set a plan later.
      });
  }, []);

  const handleCreate = () => {
    setSelectedCompany(null);
    form.resetFields();
    // Sensible defaults for a new company.
    form.setFieldsValue({ billingCycle: 'YEARLY' });
    setModalVisible(true);
  };

  const handleEdit = (record: AdminCompany) => {
    setSelectedCompany(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleView = async (record: AdminCompany) => {
    setSelectedCompany(record);
    setDrawerVisible(true);
    try {
      const statsRes = await superAdminApi.getCompanyStats(record.id);
      if (statsRes.success) {
        setCompanyStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const submitCompany = async (values: any) => {
    try {
      const payload = {
        ...values,
        ...(values.code ? { code: String(values.code).trim().toUpperCase() } : {}),
      };
      if (selectedCompany) {
        // planId/billingCycle only apply at creation time.
        const { planId: _p, billingCycle: _b, ...companyOnly } = payload;
        await superAdminApi.updateCompany(selectedCompany.id, companyOnly);
        message.success('Company updated successfully');
      } else {
        const res: any = await superAdminApi.createCompany(payload);
        // The server reports which plan was assigned, or that assignment failed
        // while the company was still created — show that rather than a generic
        // success message.
        message.success(res?.message || 'Company created successfully');
      }
      setModalVisible(false);
      fetchCompanies();
    } catch (error: any) {
      // Surface the server's reason (e.g. duplicate Company ID) rather than a generic failure.
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Operation failed'
      );
    }
  };

  const handleSubmit = async (values: any) => {
    const nextCode = String(values.code || '').trim().toUpperCase();
    const currentCode = String(selectedCompany?.code || '').toUpperCase();

    // Changing an existing company's ID changes the login key for all of its
    // users, so confirm before saving.
    if (selectedCompany && nextCode && nextCode !== currentCode) {
      Modal.confirm({
        title: 'Change Company ID?',
        content: (
          <div>
            <p>
              The Company ID for <strong>{selectedCompany.name}</strong> will change from{' '}
              <strong>{currentCode || '—'}</strong> to <strong>{nextCode}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              Everyone in that company must use the new Company ID the next time they sign in.
            </p>
          </div>
        ),
        okText: 'Change Company ID',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => submitCompany(values),
      });
      return;
    }

    await submitCompany(values);
  };

  const handleToggleActive = async (record: AdminCompany) => {
    try {
      await superAdminApi.toggleCompanyActive(record.id, !record.isActive);
      message.success(`Company ${record.isActive ? 'deactivated' : 'activated'}`);
      fetchCompanies();
    } catch (error: any) {
      message.error(error.message || 'Failed to update status');
    }
  };

  const handleDelete = (record: AdminCompany) => {
    Modal.confirm({
      title: 'Deactivate Company',
      content: `Are you sure you want to deactivate "${record.name}"? The company and all its users will be deactivated and unable to login until reactivated.`,
      okText: 'Deactivate',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await superAdminApi.deleteCompany(record.id);
          message.success('Company deactivated successfully');
          fetchCompanies();
        } catch (error: any) {
          message.error(error.message || 'Failed to delete company');
        }
      }
    });
  };

  const columns: ColumnsType<AdminCompany> = [
    {
      title: 'Company',
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.code}</Text>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '-'
    },
    {
      title: 'Users',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 80,
      align: 'center'
    },
    {
      title: 'Subscription',
      key: 'subscription',
      render: (_, record) => {
        if (!record.subscription) {
          return <Tag color="default">No subscription</Tag>;
        }
        const statusColor = {
          ACTIVE: 'green',
          PAST_DUE: 'orange',
          SUSPENDED: 'red',
          CANCELLED: 'default'
        }[record.subscription.status] || 'default';

        return (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{record.subscription.planName || record.subscription.plan}</Tag>
            <Tag color={statusColor}>{record.subscription.status}</Tag>
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
          />
        </Space>
      )
    }
  ];

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'name' as const,
    secondaryField: 'code' as const,
    statusField: 'isActive' as const,
    statusConfig: {
      true: { color: 'green', text: 'Active' },
      false: { color: 'red', text: 'Inactive' },
    },
    avatar: { icon: <BankOutlined /> },
    fields: [
      { key: 'email' as const, label: 'Email', render: (v: string) => v || '-' },
      { key: 'userCount' as const, label: 'Users' },
      {
        key: 'subscription' as const,
        label: 'Plan',
        render: (sub: AdminCompany['subscription']) =>
          sub ? sub.planName || sub.plan : 'No plan'
      },
    ],
    onClick: (company: AdminCompany) => handleView(company),
  };

  // Mobile view
  if (isMobile) {
    return (
      <div>
        {/* Mobile Header */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <Title level={4} style={{ margin: 0 }}>
            <BankOutlined /> Companies
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchCompanies()} loading={loading} />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add
            </Button>
          </Space>
        </div>

        {/* Mobile Data List */}
        <MobileDataList
          data={companies}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No companies found"
          searchPlaceholder="Search companies..."
          onSearch={(value) => {
            setSearchText(value);
            fetchCompanies({ page: 1 });
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => {
              fetchCompanies({ page, limit: pageSize });
            },
          }}
          swipeActions={{
            left: {
              icon: <DeleteOutlined />,
              color: '#ff4d4f',
              label: 'Delete',
              onClick: (company) => handleDelete(company),
            },
            right: {
              icon: <EditOutlined />,
              color: '#1890ff',
              label: 'Edit',
              onClick: (company) => handleEdit(company),
            },
          }}
        />

        {/* Create/Edit Modal - Full screen on mobile */}
        <Modal
          title={selectedCompany ? 'Edit Company' : 'Create Company'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width="100%"
          style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
          styles={{ body: { padding: '16px' } }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="Enter company name" />
            </Form.Item>
            <Form.Item
              name="code"
              label="Company ID"
              rules={[
                { required: true, message: 'Please enter Company ID' },
                {
                  pattern: /^[A-Za-z0-9][A-Za-z0-9-]{2,19}$/,
                  message: 'Use 3-20 characters: letters, numbers and dashes only',
                },
              ]}
              extra={
                selectedCompany
                  ? 'Everyone in this company signs in with this ID. Changing it means all their users must use the new ID.'
                  : 'The company signs in with this ID.'
              }
            >
              <Input
                placeholder="Enter unique Company ID"
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
            {!selectedCompany && (
              <>
                <Form.Item
                  name="planId"
                  label="Subscription Plan"
                  extra="Assigns the plan immediately. Leave empty to set it later."
                >
                  <Select
                    allowClear
                    placeholder="Select a plan (optional)"
                    options={plans.map((p) => ({
                      value: p.id,
                      label: p.displayName || p.name,
                    }))}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.planId !== cur.planId}>
                  {({ getFieldValue }) =>
                    getFieldValue('planId') ? (
                      <Form.Item name="billingCycle" label="Billing Cycle">
                        <Select
                          options={[
                            { value: 'YEARLY', label: 'Yearly' },
                            { value: 'MONTHLY', label: 'Monthly' },
                          ]}
                        />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </>
            )}
            <Form.Item name="email" label="Email">
              <Input placeholder="Enter email" type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Phone">
              <Input placeholder="Enter phone" />
            </Form.Item>
            <Form.Item name="address" label="Address">
              <Input.TextArea placeholder="Enter address" rows={2} />
            </Form.Item>
          </Form>
        </Modal>

        {/* Company Details Drawer - Full width on mobile */}
        <Drawer
          title={selectedCompany?.name}
          placement="bottom"
          height="90vh"
          open={drawerVisible}
          onClose={() => {
            setDrawerVisible(false);
            setCompanyStats(null);
          }}
        >
          {selectedCompany && (
            <>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Name">{selectedCompany.name}</Descriptions.Item>
                <Descriptions.Item label="Code">{selectedCompany.code}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedCompany.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedCompany.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="Address">{selectedCompany.address || '-'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={selectedCompany.isActive ? 'green' : 'red'}>
                    {selectedCompany.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>

              {selectedCompany.subscription && (
                <>
                  <Title level={5} style={{ marginTop: 16 }}>Subscription</Title>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="Plan">
                      {selectedCompany.subscription.planName || selectedCompany.subscription.plan}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={selectedCompany.subscription.status === 'ACTIVE' ? 'green' : 'orange'}>
                        {selectedCompany.subscription.status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount">
                      ${selectedCompany.subscription.amount}/{selectedCompany.subscription.billingCycle}
                    </Descriptions.Item>
                  </Descriptions>
                </>
              )}

              {companyStats && (
                <>
                  <Title level={5} style={{ marginTop: 16 }}>Statistics</Title>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <Card size="small" bodyStyle={{ padding: 8, textAlign: 'center' }}>
                        <Statistic title="Users" value={companyStats.counts?.users || 0} valueStyle={{ fontSize: 18 }} />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" bodyStyle={{ padding: 8, textAlign: 'center' }}>
                        <Statistic title="Customers" value={companyStats.counts?.customers || 0} valueStyle={{ fontSize: 18 }} />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" bodyStyle={{ padding: 8, textAlign: 'center' }}>
                        <Statistic title="Products" value={companyStats.counts?.products || 0} valueStyle={{ fontSize: 18 }} />
                      </Card>
                    </Col>
                  </Row>
                </>
              )}

              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <Button block onClick={() => handleEdit(selectedCompany)}>
                  <EditOutlined /> Edit
                </Button>
                <Button
                  block
                  danger={selectedCompany.isActive}
                  onClick={() => {
                    handleToggleActive(selectedCompany);
                    setDrawerVisible(false);
                  }}
                >
                  {selectedCompany.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </>
          )}
        </Drawer>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <BankOutlined /> Company Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search companies..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchCompanies()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Company
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} companies`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedCompany ? 'Edit Company' : 'Create Company'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Enter company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Company ID"
                rules={[
                  { required: true, message: 'Please enter Company ID' },
                  {
                    pattern: /^[A-Za-z0-9][A-Za-z0-9-]{2,19}$/,
                    message: 'Use 3-20 characters: letters, numbers and dashes only',
                  },
                ]}
                extra={
                  selectedCompany
                    ? 'Everyone in this company signs in with this ID. Changing it means all their users must use the new ID.'
                    : 'The company signs in with this ID.'
                }
              >
                <Input
                  placeholder="Enter unique Company ID"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
          </Row>
          {!selectedCompany && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="planId"
                  label="Subscription Plan"
                  extra="Assigns the plan immediately. Leave empty to set it later from Subscription Management."
                >
                  <Select
                    allowClear
                    placeholder="Select a plan (optional)"
                    options={plans.map((p) => ({
                      value: p.id,
                      label: p.displayName || p.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.planId !== cur.planId}>
                  {({ getFieldValue }) =>
                    getFieldValue('planId') ? (
                      <Form.Item name="billingCycle" label="Billing Cycle">
                        <Select
                          options={[
                            { value: 'YEARLY', label: 'Yearly' },
                            { value: 'MONTHLY', label: 'Monthly' },
                          ]}
                        />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input placeholder="Enter email" type="email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="Enter phone" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address">
            <Input.TextArea placeholder="Enter address" rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Company Details Drawer */}
      <Drawer
        title={`Company Details: ${selectedCompany?.name}`}
        placement="right"
        width={600}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setCompanyStats(null);
        }}
      >
        {selectedCompany && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Name">{selectedCompany.name}</Descriptions.Item>
              <Descriptions.Item label="Code">{selectedCompany.code}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedCompany.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedCompany.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Address">{selectedCompany.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedCompany.isActive ? 'green' : 'red'}>
                  {selectedCompany.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(selectedCompany.createdAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {selectedCompany.subscription && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Subscription</Title>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Plan">
                    {selectedCompany.subscription.planName || selectedCompany.subscription.plan}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={selectedCompany.subscription.status === 'ACTIVE' ? 'green' : 'orange'}>
                      {selectedCompany.subscription.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Billing Cycle">
                    {selectedCompany.subscription.billingCycle}
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount">
                    ${selectedCompany.subscription.amount}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period End">
                    {new Date(selectedCompany.subscription.currentPeriodEnd).toLocaleDateString()}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {companyStats && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Statistics</Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic title="Users" value={companyStats.counts?.users || 0} prefix={<TeamOutlined />} />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic title="Customers" value={companyStats.counts?.customers || 0} />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic title="Products" value={companyStats.counts?.products || 0} />
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default CompanyManagement;
